/**
 * Director playbook store — makes `director.md` visible + editable from
 * the Koko control panel at runtime — PLUS the WORKFLOWS model
 * (2026-07-04 yap: the playbook expands into discrete, saveable,
 * reusable multi-step routines Koko can execute on request).
 *
 * `director.md` is Koko's directing brain. The file ships as the default
 * (imported `?raw` at build time), but the user can edit it live from the
 * Koko panel. Edits can't be written back to disk from the browser, so we
 * persist an OVERRIDE in localStorage and prefer it when present. Reset
 * drops the override and falls back to the shipped file.
 *
 * WORKFLOWS ship with a starter set (script ingest, voice casting,
 * storyboarding, first cut, mood pass). The user can view, edit, save
 * new, and delete workflows from the Koko panel's Workflows tab; the
 * full effective list is serialized into the system prompt beneath the
 * playbook, so Koko can execute any of them by name using its existing
 * studio tools — no bridge change required.
 *
 * StudioKokoBridge reads `getEffectiveDirectorPlaybook()` when building the
 * system prompt, so an edit changes how Koko directs WITHOUT a rebuild.
 */

import directorPlaybookDefault from "./director.md?raw";

const STORAGE_KEY = "koko_director_playbook";
const WORKFLOWS_STORAGE_KEY = "koko_workflows";

type PlaybookListener = (playbook: string) => void;

const listeners = new Set<PlaybookListener>();

/** The shipped `director.md`, trimmed of trailing whitespace. */
export function getDefaultDirectorPlaybook(): string {
  return directorPlaybookDefault.trim();
}

/** True when the user has saved a custom override that differs from default. */
export function isDirectorPlaybookCustomized(): boolean {
  const override = readOverride();
  return override !== null && override.trim() !== getDefaultDirectorPlaybook();
}

/**
 * The playbook Koko actually runs with: the saved override if present,
 * otherwise the shipped default — ALWAYS followed by the serialized
 * workflow catalog so Koko can look up and execute saved workflows by
 * name. The bridge consumes this single string, which is how workflows
 * reach the system prompt without any frozen-file change.
 */
export function getEffectiveDirectorPlaybook(): string {
  const override = readOverride();
  const playbook =
    override !== null && override.trim().length > 0
      ? override.trim()
      : getDefaultDirectorPlaybook();
  return `${playbook}\n\n${buildWorkflowsPromptSection()}`;
}

/** Raw text to seed the editor textarea (override verbatim, else default). */
export function readDirectorPlaybookForEditor(): string {
  const override = readOverride();
  return override !== null ? override : getDefaultDirectorPlaybook();
}

/**
 * Save an edited playbook. Saving text identical to the default clears the
 * override so the panel reflects "default" again. Empty input also resets.
 */
export function writeDirectorPlaybook(text: string): void {
  const trimmed = text.trim();
  if (trimmed.length === 0 || trimmed === getDefaultDirectorPlaybook()) {
    resetDirectorPlaybook();
    return;
  }
  persist(trimmed);
  notify();
}

/** Drop the override and fall back to the shipped `director.md`. */
export function resetDirectorPlaybook(): void {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // privacy-mode Safari / quota — ignore; nothing to remove.
    }
  }
  notify();
}

/** Subscribe to playbook changes (save/reset). Returns an unsubscribe fn. */
export function subscribeDirectorPlaybook(listener: PlaybookListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function readOverride(): string | null {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function persist(text: string): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, text);
  } catch {
    // Quota / privacy-mode — the edit applies for subscribers this session;
    // it just won't survive a reload.
  }
}

function notify(): void {
  const next = getEffectiveDirectorPlaybook();
  for (const listener of listeners) listener(next);
}

// ====================================================================
// WORKFLOWS — named, reusable multi-step routines Koko can execute
// (2026-07-04 yap). Stored as a full-list localStorage override; the
// shipped starter set is the default. Every mutation notifies both the
// workflow listeners AND the playbook listeners (the effective
// playbook string embeds the workflow catalog).
// ====================================================================

export interface KokoWorkflow {
  /** Stable id (slug). Starter ids are prefixed `starter-`. */
  id: string;
  /** Display name the user (or Koko) invokes the workflow by. */
  name: string;
  /** One-line summary of what running it achieves. */
  description: string;
  /** Ordered steps, phrased as instructions Koko executes with its studio tools. */
  steps: string[];
}

type WorkflowsListener = (workflows: readonly KokoWorkflow[]) => void;
const workflowListeners = new Set<WorkflowsListener>();

/**
 * Starter set — useful out of the box, and worked examples for the
 * user's own workflows. Steps reference the studio tools + panels that
 * exist today so Koko can execute them without guessing.
 */
export const STARTER_WORKFLOWS: readonly KokoWorkflow[] = [
  {
    id: "starter-ingest-script",
    name: "Ingest a script and populate everything",
    description:
      "Take the uploaded script and fill the whole workspace: characters, locations, assets, casting.",
    steps: [
      "Confirm a script is loaded (scenes exist in the workspace state). If not, ask the user to upload one via the script column's import control.",
      "For every character named in the script that is missing from the cast, call add_character with a concrete physical description drawn from the script.",
      "For every slug-line location that is missing, call add_location with a physical description (layout, materials, scale — never vibes).",
      "For every prop or vehicle the script depends on, call add_asset with the right type.",
      "Casting derives from dialogue automatically; call cast_scene only to override a scene whose derived cast is wrong.",
      "Finish with a one-sentence recap of what was created and suggest the next step (usually the storyboard).",
    ],
  },
  {
    id: "starter-cast-voices",
    name: "Cast voices for all characters",
    description:
      "Give every character a voice profile so dialogue lines can be spoken.",
    steps: [
      "Open the Characters panel with [OPEN:characters].",
      "For each character without a voice, pick a fitting voice profile in its card's voice control (point at the control, then set it).",
      "If a character's description is too thin to cast a voice, call update_character first to enrich it.",
      "Recap which characters got which voice and flag any you were unsure about.",
    ],
  },
  {
    id: "starter-storyboard-arc",
    name: "Storyboard arc 1",
    description:
      "Board the opening arc: framing per scene, then mark the storyboard ready.",
    steps: [
      "Identify the scenes of the first arc (roughly the first third of the script).",
      "For each of those scenes, call set_scene_camera with framing that serves the scene's intent (wide establishing for new locations, tighter for dialogue).",
      "Open the Storyboard panel with [OPEN:storyboard] and review the boards.",
      "Call set_storyboard_status with status ready once every arc-1 scene has framing.",
      "Recap the boarded scenes and suggest boarding the next arc.",
    ],
  },
  {
    id: "starter-first-cut",
    name: "Assemble a first cut",
    description:
      "Line up everything needed for a first render and kick off the assembly.",
    steps: [
      "Verify prerequisites: scenes exist, every scene has cast + location, and the storyboard status is ready. List anything missing instead of proceeding blindly.",
      "Call jump_timeline_track with track_id video to bring up the assembly view.",
      "Walk the timeline beats in order and confirm each scene has its pieces; fix small gaps with the matching tool as you go.",
      "Open the Final Video panel with [OPEN:final_video] and start the assembly.",
      "Narrate progress briefly and celebrate the premiere when it renders.",
    ],
  },
  {
    id: "starter-mood-pass",
    name: "Mood pass on the film's look",
    description:
      "Pin a coherent visual direction to the mood board from the script's tone.",
    steps: [
      "Read the script's tone (time of day, weather, emotional register of key scenes).",
      "Call add_moodboard_item three to five times: one palette entry, one or two image references, and one or two notes — each with a concrete, physical detail.",
      "Keep all descriptions physical (light quality, color temperature, texture), never vague vibes.",
      "Recap the direction in one sentence and ask if the user wants it pushed into location descriptions.",
    ],
  },
];

/** The workflows Koko actually runs with (override if saved, else starters). */
export function getWorkflows(): readonly KokoWorkflow[] {
  const raw = readRawStorage(WORKFLOWS_STORAGE_KEY);
  if (raw === null) return STARTER_WORKFLOWS;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return STARTER_WORKFLOWS;
    const valid = parsed.filter(isValidWorkflow);
    return valid.length > 0 ? valid : STARTER_WORKFLOWS;
  } catch {
    return STARTER_WORKFLOWS;
  }
}

/** Look a workflow up by id, or by case-insensitive name. */
export function findWorkflow(idOrName: string): KokoWorkflow | null {
  const needle = idOrName.trim().toLowerCase();
  for (const workflow of getWorkflows()) {
    if (workflow.id === idOrName || workflow.name.toLowerCase() === needle) {
      return workflow;
    }
  }
  return null;
}

/**
 * Create or update a workflow (matched by id). Blank names/steps are
 * rejected so the catalog can never hold an unexecutable entry.
 * Returns the saved workflow, or null when the input was invalid.
 */
export function saveWorkflow(
  input: Omit<KokoWorkflow, "id"> & { id?: string },
): KokoWorkflow | null {
  const name = input.name.trim();
  const steps = input.steps.map((step) => step.trim()).filter((s) => s.length > 0);
  if (name.length === 0 || steps.length === 0) return null;
  const workflow: KokoWorkflow = {
    id: input.id?.trim() || slugify(name),
    name,
    description: input.description.trim(),
    steps,
  };
  const current = [...getWorkflows()];
  const existingIndex = current.findIndex((w) => w.id === workflow.id);
  if (existingIndex >= 0) current[existingIndex] = workflow;
  else current.push(workflow);
  persistWorkflows(current);
  return workflow;
}

/** Remove a workflow by id. Removing the last one restores the starters. */
export function deleteWorkflow(id: string): void {
  const remaining = getWorkflows().filter((w) => w.id !== id);
  persistWorkflows(remaining);
}

/** Drop all customizations and restore the shipped starter set. */
export function resetWorkflows(): void {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.removeItem(WORKFLOWS_STORAGE_KEY);
    } catch {
      // privacy-mode Safari — nothing to remove.
    }
  }
  notifyWorkflows();
}

/** True when the saved list differs from the shipped starters. */
export function areWorkflowsCustomized(): boolean {
  return readRawStorage(WORKFLOWS_STORAGE_KEY) !== null;
}

export function subscribeWorkflows(listener: WorkflowsListener): () => void {
  workflowListeners.add(listener);
  return () => workflowListeners.delete(listener);
}

/**
 * Serialize the workflow catalog for the system prompt. Koko is told
 * these are executable-on-request routines; each is rendered with its
 * numbered steps so execution needs no further lookup.
 */
export function buildWorkflowsPromptSection(): string {
  const workflows = getWorkflows();
  const lines: string[] = [
    "## WORKFLOWS (saved, reusable routines — execute on request)",
    'The user can ask you to run any workflow below by name (e.g. "run the storyboard arc 1 workflow").',
    "When asked, execute its steps IN ORDER with your studio tools, narrating briefly as you go.",
    "If a step's precondition fails, say exactly what's missing instead of pretending it succeeded.",
    "The user manages these in your panel's Workflows tab (view, edit, save new ones).",
    "",
  ];
  workflows.forEach((workflow, index) => {
    lines.push(`### ${index + 1}. ${workflow.name}`);
    if (workflow.description) lines.push(workflow.description);
    workflow.steps.forEach((step, stepIndex) => {
      lines.push(`${stepIndex + 1}. ${step}`);
    });
    lines.push("");
  });
  return lines.join("\n").trim();
}

function persistWorkflows(workflows: readonly KokoWorkflow[]): void {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.setItem(
        WORKFLOWS_STORAGE_KEY,
        JSON.stringify(workflows),
      );
    } catch {
      // Quota / privacy mode — the change applies in-session only.
    }
  }
  notifyWorkflows();
}

function notifyWorkflows(): void {
  const next = getWorkflows();
  for (const listener of workflowListeners) listener(next);
  // The prompt embeds the catalog, so playbook subscribers refresh too.
  notify();
}

function isValidWorkflow(candidate: unknown): candidate is KokoWorkflow {
  if (typeof candidate !== "object" || candidate === null) return false;
  const record = candidate as Record<string, unknown>;
  return (
    typeof record.id === "string" &&
    record.id.length > 0 &&
    typeof record.name === "string" &&
    record.name.trim().length > 0 &&
    typeof record.description === "string" &&
    Array.isArray(record.steps) &&
    record.steps.length > 0 &&
    record.steps.every((step) => typeof step === "string")
  );
}

function slugify(name: string): string {
  const base = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
  return base.length > 0 ? base : `workflow-${Date.now().toString(36)}`;
}

function readRawStorage(key: string): string | null {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
